import React, { useState, useMemo, memo, useCallback, useRef, useEffect } from 'react';
import { Input, Empty, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { FixedSizeList as List } from 'react-window';
import { Ambassador } from '../../types';
import AmbassadorCard from './AmbassadorCard';

const { Search } = Input;

interface CardGridProps {
  ambassadors: Ambassador[];
  selectedAmbassador?: Ambassador | null;
  onAmbassadorSelect?: (ambassador: Ambassador) => void;
  loading?: boolean;
}

const CardGrid: React.FC<CardGridProps> = ({
  ambassadors,
  selectedAmbassador,
  onAmbassadorSelect,
  loading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [containerHeight, setContainerHeight] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);

  // Measure container height dynamically
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerHeight(rect.height);
      }
    };

    // Initial measurement
    updateHeight();

    // Set up ResizeObserver for dynamic updates
    const resizeObserver = new ResizeObserver(updateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Filter ambassadors based on search query
  const filteredAmbassadors = useMemo(() => {
    if (!searchQuery.trim()) {
      return ambassadors;
    }

    const query = searchQuery.toLowerCase();
    return ambassadors.filter((ambassador) => {
      const searchableText = [
        ambassador.fullName,
        ambassador.city,
        ambassador.country,
        ambassador.businessUnit,
        ambassador.activity,
        ambassador.role,
        ambassador.region,
      ].join(' ').toLowerCase();
      
      return searchableText.includes(query);
    });
  }, [ambassadors, searchQuery]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  const handleAmbassadorClick = (ambassador: Ambassador) => {
    if (selectedAmbassador?.id === ambassador.id) {
      // Deselect if clicking the same ambassador
      onAmbassadorSelect?.(null);
    } else {
      // Select the new ambassador
      onAmbassadorSelect?.(ambassador);
    }
  };

  // Virtual list item renderer for performance
  const renderRow = useCallback(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const ambassador = filteredAmbassadors[index];
    return (
      <div style={{ ...style, padding: '2px 0' }}>
        <AmbassadorCard
          key={ambassador.id}
          ambassador={ambassador}
          highlighted={selectedAmbassador?.id === ambassador.id}
          onClick={handleAmbassadorClick}
        />
      </div>
    );
  }, [filteredAmbassadors, selectedAmbassador, handleAmbassadorClick]);

  return (
    <div 
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      data-testid="ambassador-directory"
    >
      {/* Search Input */}
      <div style={{ marginBottom: '16px' }}>
        <Search
          placeholder="Search ambassadors by name, location, or activity..."
          allowClear
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#999' }} />}
          style={{ width: '100%' }}
        />
      </div>

      {/* Results Count */}
      <div style={{ marginBottom: '12px', fontSize: '12px', color: '#666' }}>
        {searchQuery ? (
          <>
            Showing {filteredAmbassadors.length} of {ambassadors.length} ambassadors
            {searchQuery && (
              <span> matching "{searchQuery}"</span>
            )}
          </>
        ) : (
          `${ambassadors.length} ambassadors`
        )}
      </div>

      {/* Virtual Cards Container for Performance */}
      <div ref={containerRef} style={{ flex: 1, minHeight: 0 }}>
        {filteredAmbassadors.length === 0 ? (
          <Empty
            description={
              searchQuery 
                ? `No ambassadors found matching "${searchQuery}"`
                : "No ambassadors found"
            }
            style={{ marginTop: '40px' }}
          />
        ) : (
          <List
            height={containerHeight} // Use measured container height
            itemCount={filteredAmbassadors.length}
            itemSize={80} // Card height (~62px) + padding + comfortable spacing
            width="100%"
            overscanCount={5} // Render 5 extra items for smooth scrolling
          >
            {renderRow}
          </List>
        )}
      </div>
    </div>
  );
};

// Memoize CardGrid to prevent unnecessary re-renders
export default memo(CardGrid);